import { Component } from "@lisergia/core";
import { Viewport } from "@lisergia/managers";
import { DOMUtils, MathUtils } from "@lisergia/utilities";

export function damp(x: number, y: number, t: number, delta: number): number {
  return interpolate(
    x,
    y,
    1 - Math.exp(Math.log(1 - t) * (delta / (1000 / 60))),
  );
}

export function interpolate(x: number, y: number, t: number): number {
  return (1 - t) * x + t * y;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export interface MediaElement {
  isBefore: boolean;
  isAfter: boolean;

  extra: number;
  offsetLeft: number;
  left: number;
  position: number;
  width: number;
}

const FRICTION = 0.72;
const DAMPING = 0.12;

export default class extends Component {
  declare element: HTMLDivElement;

  declare elements: {
    list: HTMLDivElement;
    items: NodeListOf<HTMLDivElement>;
    links: NodeListOf<HTMLAnchorElement>;
    pagination: HTMLDivElement;
    paginationButtons: NodeListOf<HTMLButtonElement>;
    buttonPrevious: HTMLButtonElement;
    buttonNext: HTMLButtonElement;
    progress: HTMLDivElement;
  };

  isInfinite: boolean = true;

  itemsData: MediaElement[] = [];

  width: number = 0;
  widthItem: number = 0;
  widthClamp: number = 0;

  constructor({ element }: { element: HTMLElement }) {
    super({
      element,
      elements: {
        list: "[data-carousel-list]",
        items: "[data-carousel-item]",
        links: element.querySelectorAll("a") as NodeListOf<HTMLAnchorElement>,
        pagination: element.querySelector(
          "[data-carousel-pagination]",
        ) as HTMLDivElement,
        paginationButtons: element.querySelectorAll(
          "[data-carousel-pagination-button]",
        ) as NodeListOf<HTMLButtonElement>,
        buttonPrevious: element.querySelector(
          "[data-carousel-button-previous]",
        ) as HTMLButtonElement,
        buttonNext: element.querySelector(
          "[data-carousel-button-next]",
        ) as HTMLButtonElement,
        progress: element.querySelector(
          "[data-carousel-progress]",
        ) as HTMLDivElement,
      },
    });

    this.isInfinite = !this.element.hasAttribute("data-carousel-finite");

    window.requestAnimationFrame(this.onLoop);

    this.addEventListeners();
  }

  isDown: boolean = false;

  start: number = 0;

  distanceMoved: number = 0;

  onLinkClick(event: MouseEvent) {
    if (this.distanceMoved > 10) {
      event.preventDefault();
    }
  }

  onTouchDown(event: MouseEvent | TouchEvent) {
    if (event instanceof MouseEvent) {
      event.preventDefault();
    }

    this.isDown = true;
    this.distanceMoved = 0;

    this.scroll.position = this.scroll.current;
    this.start =
      event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
  }

  onTouchMove(event: MouseEvent | TouchEvent) {
    if (!this.isDown) return;

    const x =
      event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
    const delta = this.start - x;

    this.distanceMoved += Math.abs(delta);

    this.scroll.target += delta;
    this.scroll.velocity += delta;

    this.start = x;
  }

  onTouchUp() {
    this.isDown = false;

    this.scroll.velocity *= 2;

    this.onCheck();
  }

  onCheck() {
    const resting = this.scroll.target + this.scroll.velocity / (1 - FRICTION);
    const x = Math.round(resting / this.widthItem) * this.widthItem;

    const distance = x - this.scroll.target;
    const force = distance * (1 - FRICTION) * (1 / FRICTION);

    this.scroll.velocity = force;
  }

  previous() {
    this.scroll.target -= this.widthItem;
  }

  next() {
    this.scroll.target += this.widthItem;
  }

  direction: "right" | "left" = "right";

  scroll = {
    current: 0,
    ease: 0.05,
    last: 0,
    position: 0,
    target: 0,
    velocity: 0,
  };

  frameDelta = 0;

  frameTick = 0;

  index = -1;

  onLoopScroll() {
    this.scroll.velocity *= FRICTION;

    if (!this.isDown) {
      this.scroll.target += this.scroll.velocity;
    }

    if (!this.isInfinite) {
      this.scroll.target = clamp(this.scroll.target, 0, this.widthClamp);
    }

    if (!this.isDown) {
      this.scroll.current = damp(
        this.scroll.current,
        this.scroll.target,
        DAMPING,
        this.frameDelta,
      );
    } else {
      this.scroll.current = damp(
        this.scroll.current,
        this.scroll.target,
        FRICTION,
        this.frameDelta,
      );
    }

    const length = this.itemsData.length;
    let index =
      Math.floor(this.scroll.current + this.widthItem / 2) % this.width;

    index = Math.floor((index / this.width) * length);

    if (index < 0) {
      index = (length - Math.abs(index % length)) % length;
    }

    if (this.index !== index) {
      this.index = index;

      if (
        this.element.classList.contains("favorites__content") ||
        this.element.classList.contains("testimonials__content") ||
        this.element.classList.contains("subjects")
      ) {
        this.index = this.index % (this.itemsData.length / 2);
      }

      if (this.elements.pagination) {
        const active = this.elements.pagination.dataset.carouselPagination!;

        this.elements.paginationButtons.forEach((button, buttonIndex) => {
          if (buttonIndex === this.index) {
            button.classList.add(active);
          } else {
            button.classList.remove(active);
          }
        });
      }
    }

    if (this.elements.progress) {
      const progress = MathUtils.map(
        this.scroll.current,
        0,
        this.widthClamp,
        0,
        1,
      );

      this.elements.progress.style.setProperty("--progress", `${progress}`);
    }

    if (this.scroll.current > this.scroll.last) {
      this.direction = "right";
    } else {
      this.direction = "left";
    }
  }

  onLoopMedias() {
    this.itemsData.forEach((itemData, index) => {
      const media = this.elements.items[index];

      itemData.position = -this.scroll.current - itemData.extra;

      if (this.isInfinite) {
        const left = itemData.position + itemData.left + itemData.width;

        itemData.isBefore = left < 0;
        itemData.isAfter = left > this.width;

        if (this.direction === "right" && itemData.isBefore) {
          itemData.extra -= this.width;

          itemData.isBefore = false;
          itemData.isAfter = false;
        }

        if (this.direction === "left" && itemData.isAfter) {
          itemData.extra += this.width;

          itemData.isBefore = false;
          itemData.isAfter = false;
        }
      }

      media.style.transform = `translateX(${itemData.position}px)`;

      if (this.element.classList.contains("stats__content")) {
        const x = itemData.offsetLeft - itemData.extra - this.scroll.current;
        const positionPercent =
          x > 0
            ? MathUtils.map(x, 0, this.width / 2 / 2, 1, 0)
            : MathUtils.map(x, -this.width / 2 / 2, 0, 0, 1);

        const blur = MathUtils.map(positionPercent, 0, 1, 2, 0);
        const scale = MathUtils.map(positionPercent, 0, 1, 0.25, 1);
        const opacity = MathUtils.map(positionPercent, 0, 1, 0.25, 1);

        const translateY = MathUtils.map(positionPercent, 0, 1, 40, 0);

        media.style.transform = `translateX(${itemData.position}px) translateY(-${translateY}rem) scale(${scale})`;
        media.style.opacity = `${opacity}`;
        media.style.filter = `blur(${blur}rem)`;
      }

      if (this.element.classList.contains("favorites__content")) {
        const x = itemData.offsetLeft - itemData.extra - this.scroll.current;

        const width = this.width / 2 / 2;

        const normalizedDistance = Math.abs(x) / width;
        const exponentialFactor = Math.pow(normalizedDistance, 2.5);

        const translateXValue = Viewport.isPhone ? 25 : 40;
        const translateX =
          MathUtils.map(x, -width, width, translateXValue, -translateXValue) *
          exponentialFactor;

        const translateYValue = Viewport.isPhone ? 8 : 10;
        const translateY = MathUtils.map(
          Math.abs(x),
          0,
          width,
          0,
          translateYValue,
        );

        const rotateValue = Viewport.isPhone ? 7.5 : 10;
        const rotate = MathUtils.map(
          x,
          -width,
          width,
          -rotateValue,
          rotateValue,
        );

        const scale = 1 - exponentialFactor * 0.4;

        const z = MathUtils.map(Math.abs(x), 0, width, -1, 1);

        media.style.opacity = `${MathUtils.map(Math.abs(x), width * 0.9, width, 1, 0)}`;
        media.style.transform = `translateX(${itemData.position}px) translateX(${translateX}rem) translateY(${translateY}rem) translateZ(${z}rem) rotate(${rotate}deg) scale(${scale})`;
        media.style.zIndex = `${Math.floor((1 - Math.abs(x) / width) * 100)}`;
      }

      if (this.element.classList.contains("testimonials__content")) {
        const x = itemData.offsetLeft - itemData.extra - this.scroll.current;
        const xAbs = Math.abs(x);

        const width = this.width / 2 / 2;

        const normalizedDistance = xAbs / width;
        const exponentialFactor = Math.pow(normalizedDistance, 2.5);

        const translateXValue = Viewport.isPhone ? 25 : 40;
        const translateX =
          MathUtils.map(
            x,
            -width,
            width,
            translateXValue,
            -translateXValue,
            true,
          ) * exponentialFactor;

        const translateYValue = Viewport.isPhone ? 8 : 5;
        const translateY = MathUtils.map(xAbs, 0, width, 0, translateYValue);

        const scale = 1 - exponentialFactor * 0.4;

        const rotateValue = Viewport.isPhone ? 7.5 : 10;
        const rotate = MathUtils.map(
          x,
          -width,
          width,
          -rotateValue,
          rotateValue,
        );

        media.style.transform = `translateX(${itemData.position}px) translateX(${translateX}rem) translateY(${translateY}rem) scale(${scale}) rotate(${rotate}deg)`;
        media.style.opacity = `${xAbs > width * 0.5 ? MathUtils.map(xAbs, width * 0.5, width, 0.2, 0) : MathUtils.map(xAbs, 0, width * 0.5, 1, 0.2)}`;

        media.style.setProperty(
          "--content",
          `${MathUtils.map(xAbs, 0, width * 0.5, 1, 0)}`,
        );
      }
    });
  }

  frameId: number = 0;

  onLoop(delta: number) {
    this.frameDelta = delta - this.frameTick;

    this.onLoopScroll();
    this.onLoopMedias();

    this.scroll.last = this.scroll.current;

    this.frameTick = delta;

    this.frameId = window.requestAnimationFrame(this.onLoop);
  }

  onResize() {
    this.elements.items.forEach((media) => {
      media.style.transform = "";
    });

    const spacing = parseFloat(
      getComputedStyle(this.elements.items[0]).paddingRight,
    );

    this.itemsData = Array.from(this.elements.items).map((media) => {
      const bounds = DOMUtils.getBounds(media);

      return {
        isBefore: false,
        isAfter: false,

        extra: 0,
        left: bounds.left,
        offsetLeft: media.offsetLeft,
        position: 0,
        width: bounds.width,
      };
    });

    this.width = this.elements.list.clientWidth;
    this.widthItem = this.itemsData[0].width;
    this.widthClamp = this.width - this.element.clientWidth - spacing;

    this.scroll = {
      current: 0,
      ease: 0.1,
      last: 0,
      position: 0,
      target: 0,
      velocity: 0,
    };
  }

  onProgressDown(event: MouseEvent) {
    event.preventDefault();

    const bounds = DOMUtils.getBounds(this.elements.progress);
    const x = event.clientX - bounds.left;
    const progress = clamp(x / bounds.width, 0, 1);

    this.scroll.target = progress * this.widthClamp;

    this.onCheck();

    window.addEventListener("mousemove", this.onProgressMove);
    window.addEventListener("mouseup", this.onProgressUp);
  }

  onProgressMove(event: MouseEvent) {
    event.preventDefault();

    const bounds = DOMUtils.getBounds(this.elements.progress);
    const x = event.clientX - bounds.left;
    const progress = clamp(x / bounds.width, 0, 1);

    this.scroll.target = progress * this.widthClamp;
  }

  onProgressUp() {
    window.removeEventListener("mousemove", this.onProgressMove);
    window.removeEventListener("mouseup", this.onProgressUp);
  }

  onPaginate(event: MouseEvent) {
    const button = event.currentTarget as HTMLButtonElement;
    const index = Array.from(this.elements.paginationButtons).indexOf(button);

    if (this.isInfinite) {
      const isDuplicated =
        this.element.classList.contains("favorites__content") ||
        this.element.classList.contains("testimonials__content") ||
        this.element.classList.contains("subjects");

      const totalItems = isDuplicated
        ? this.itemsData.length / 2
        : this.itemsData.length;
      const cycleLength = this.itemsData.length * this.widthItem;
      const currentPosition = this.scroll.current;

      // Determine which cycle we're currently in
      const currentCycle = Math.floor(currentPosition / cycleLength);

      // Base target positions within a single cycle
      const baseTargets = [index * this.widthItem];

      if (isDuplicated) {
        baseTargets.push((index + totalItems) * this.widthItem);
      }

      // Find the closest position across current, previous, and next cycles
      let closestPosition = currentPosition;
      let minDistance = Infinity;

      for (const baseTarget of baseTargets) {
        for (const cycle of [
          currentCycle - 1,
          currentCycle,
          currentCycle + 1,
        ]) {
          const candidatePosition = baseTarget + cycle * cycleLength;
          const distance = Math.abs(currentPosition - candidatePosition);

          if (distance < minDistance) {
            minDistance = distance;
            closestPosition = candidatePosition;
          }
        }
      }

      this.scroll.target = closestPosition;
    } else {
      this.scroll.target = index * this.widthItem;
    }
  }

  resizeObserver!: ResizeObserver;

  createResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.onResize();
    });

    this.resizeObserver.observe(this.element);
  }

  destroyResizeObserver() {
    this.resizeObserver.disconnect();
  }

  addEventListeners() {
    this.createResizeObserver();

    if (this.elements.list) {
      this.elements.list.addEventListener("mousedown", this.onTouchDown);
      this.elements.list.addEventListener("touchstart", this.onTouchDown);
    }

    if (this.elements.buttonPrevious) {
      this.elements.buttonPrevious.addEventListener("click", this.previous);
    }

    if (this.elements.buttonNext) {
      this.elements.buttonNext.addEventListener("click", this.next);
    }

    if (this.elements.progress) {
      this.elements.progress.addEventListener("mousedown", this.onProgressDown);
    }

    this.elements.paginationButtons.forEach((button, index) => {
      button.addEventListener("click", this.onPaginate);
    });

    this.elements.links.forEach((link) => {
      link.addEventListener("click", this.onLinkClick);
    });

    window.addEventListener("mousemove", this.onTouchMove);
    window.addEventListener("touchmove", this.onTouchMove);

    window.addEventListener("mouseup", this.onTouchUp);
    window.addEventListener("touchend", this.onTouchUp);
  }

  removeEventListeners() {
    window.cancelAnimationFrame(this.frameId);

    this.destroyResizeObserver();

    if (this.elements.list) {
      this.elements.list.removeEventListener("mousedown", this.onTouchDown);
      this.elements.list.removeEventListener("touchstart", this.onTouchDown);
    }

    if (this.elements.buttonPrevious) {
      this.elements.buttonPrevious.removeEventListener("click", this.previous);
    }

    if (this.elements.buttonNext) {
      this.elements.buttonNext.removeEventListener("click", this.next);
    }

    this.elements.paginationButtons.forEach((button, index) => {
      button.removeEventListener("click", this.onPaginate);
    });

    this.elements.links.forEach((link) => {
      link.removeEventListener("click", this.onLinkClick);
    });

    window.removeEventListener("mousemove", this.onTouchMove);
    window.removeEventListener("touchmove", this.onTouchMove);

    window.removeEventListener("mouseup", this.onTouchUp);
    window.removeEventListener("touchend", this.onTouchUp);
  }

  destroy() {
    this.removeEventListeners();
  }
}
